/** AI provider and model settings. */
const en = {
  title: 'AI & Models',
  needAdmin: 'You need an admin or owner role to manage AI settings.',
  subtitle:
    'Choose the provider and language model used to write session summaries. Changes apply immediately. No restart needed.',
  on: (provider: string, model: string) => `AI summaries are on, ${provider}, model “${model}”.`,
  off: 'AI summaries are off. Sessions fall back to the built-in heuristic. Pick a provider and add a key below.',
  section: 'Language model',
  sectionHint: 'The engine that turns a session digest into a plain-language recap.',
  provider: 'Provider',
  providers: {
    anthropic: 'Anthropic (Claude)',
    openai: 'OpenAI / compatible',
    none: 'Disabled (use built-in heuristic)',
  } as Record<string, string>,
  language: 'Summary language',
  languageAuto: 'Auto (model default)',
  languageHelpNone: 'Only applies to AI-written summaries (the built-in heuristic is English).',
  languageHelp: 'New AI summaries are written in this language. Regenerate an old one to translate it.',
  apiKey: 'API key',
  apiKeyHelp:
    'Write-only. Leave blank to keep the current key. Models load automatically once a key is set.',
  model: 'Model',
  loadingModels: 'Loading models…',
  modelHelp: 'Pick a suggestion or type any model id the provider supports.',
  modelsAvailable: (n: number) => `${n} models available for this key.`,
  modelsFailed: 'Couldn’t list models. Check the key, or type the id manually.',
  modelsFailedManual: 'Couldn’t list models. Type the id manually.',
  baseUrl: 'Base URL (optional)',
  baseUrlHelp:
    'For OpenAI-compatible gateways or local models: Azure, OpenRouter, Ollama, vLLM…',
  testing: 'Testing…',
  testConnection: 'Test connection',
  testFailed: 'Test request failed',
  disabledNote:
    'With AI disabled, ReplayOps still writes a useful summary from the detected signals using a deterministic heuristic. Good for air-gapped installs.',
  saving: 'Saving…',
  update: 'Update AI settings',
  save: 'Save AI settings',
  discard: 'Discard changes',
  saveFailed: 'Couldn’t save settings.',
  unsaved: 'You have unsaved changes.',
  savedApplied: 'Saved & applied',
  allSaved: 'All changes saved. Edit any field to update.',
};

const tr: typeof en = {
  title: 'AI ve Modeller',
  needAdmin: 'AI ayarlarını yönetmek için yönetici ya da sahip rolü gerekir.',
  subtitle:
    'Oturum özetlerini yazan sağlayıcıyı ve dil modelini seçin. Değişiklikler anında geçerli olur. Yeniden başlatma gerekmez.',
  on: (provider: string, model: string) =>
    `AI özetleri açık, ${provider}, model “${model}”.`,
  off: 'AI özetleri kapalı. Oturumlar yerleşik sezgisel yönteme düşer. Aşağıdan bir sağlayıcı seçip anahtar ekleyin.',
  section: 'Dil modeli',
  sectionHint: 'Bir oturum özetini sade dille anlatıya dönüştüren motor.',
  provider: 'Sağlayıcı',
  providers: {
    anthropic: 'Anthropic (Claude)',
    openai: 'OpenAI / uyumlu',
    none: 'Kapalı (yerleşik sezgisel yöntem)',
  },
  language: 'Özet dili',
  languageAuto: 'Otomatik (model varsayılanı)',
  languageHelpNone:
    'Yalnızca AI tarafından yazılan özetler için geçerlidir (yerleşik sezgisel yöntem İngilizcedir).',
  languageHelp:
    'Yeni AI özetleri bu dilde yazılır. Eski bir özeti çevirmek için yeniden üretin.',
  apiKey: 'API anahtarı',
  apiKeyHelp:
    'Yalnızca yazılır. Mevcut anahtarı korumak için boş bırakın. Anahtar girilince modeller otomatik yüklenir.',
  model: 'Model',
  loadingModels: 'Modeller yükleniyor…',
  modelHelp: 'Bir öneri seçin ya da sağlayıcının desteklediği herhangi bir model kimliğini yazın.',
  modelsAvailable: (n: number) => `Bu anahtar için ${n} model mevcut.`,
  modelsFailed: 'Modeller listelenemedi. Anahtarı kontrol edin ya da kimliği elle yazın.',
  modelsFailedManual: 'Modeller listelenemedi. Kimliği elle yazın.',
  baseUrl: 'Temel URL (isteğe bağlı)',
  baseUrlHelp:
    'OpenAI uyumlu ağ geçitleri ya da yerel modeller için: Azure, OpenRouter, Ollama, vLLM…',
  testing: 'Test ediliyor…',
  testConnection: 'Bağlantıyı test et',
  testFailed: 'Test isteği başarısız oldu',
  disabledNote:
    'AI kapalıyken ReplayOps yine de algılanan sinyallerden belirlenimci bir yöntemle kullanışlı bir özet yazar. Kapalı ortam kurulumları için uygundur.',
  saving: 'Kaydediliyor…',
  update: 'AI ayarlarını güncelle',
  save: 'AI ayarlarını kaydet',
  discard: 'Değişiklikleri at',
  saveFailed: 'Ayarlar kaydedilemedi.',
  unsaved: 'Kaydedilmemiş değişiklikleriniz var.',
  savedApplied: 'Kaydedildi ve uygulandı',
  allSaved: 'Tüm değişiklikler kaydedildi. Güncellemek için herhangi bir alanı düzenleyin.',
};

export const ai = { en, tr };
